import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSun,
  FiZap,
  FiTrendingUp,
  FiHome,
  FiLogIn,
  FiMap,
  FiX,
  FiUser,
  FiLock,
  FiArrowRight,
  FiRefreshCw,
  FiAlertCircle,
  FiCheckCircle,
  FiAward,
  FiUsers,
  FiActivity,
  FiFilter,
} from "react-icons/fi";
import {
  RiLeafLine,
  RiPlantLine,
  RiEarthLine,
} from "react-icons/ri";
import Chart from "react-apexcharts";
import toast from "react-hot-toast";
import isEmpty from "lodash.isempty";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setLoader } from "../../Database/Action/ConstantAction";
import { postHeaderWithoutToken } from "../../Database/Utils";
import Cookies from "universal-cookie";
import DeviceMapComponent from "../Components/DeviceMapComponent";
import "./SolarDashboard.css";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const apiEndpoints = {
  devices: `${API_BASE_URL}/getNewDeviceList`,
  carbonDashboard: `${API_BASE_URL}/getCarbonCreditDashboard`,
};

// ── Login Modal ──────────────────────────────────────────────────────────────
const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const navigate = useNavigate();
  const [loginInfo, setLoginInfo] = useState({ userName: "", password: "", rememberMe: false });
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = () => {
    if (isEmpty(loginInfo.userName)) { toast.error("Failed! Please Enter UserName"); return; }
    if (isEmpty(loginInfo.password)) { toast.error("Failed! Please Enter Password"); return; }
    setIsLoading(true);
    dispatch(setLoader(true));
    let formData = new FormData();
    formData.append("userId", loginInfo.userName);
    formData.append("pass", loginInfo.password);
    axios.post(process.env.REACT_APP_BASE_URL + "/loginAdmin", formData, postHeaderWithoutToken)
      .then((res) => {
        dispatch(setLoader(false));
        setIsLoading(false);
        cookies.remove("adminToken");
        cookies.remove("rememberMe");
        cookies.set("adminToken", res?.data?.token, { path: "/", maxAge: loginInfo.rememberMe ? 30 * 24 * 60 * 60 : undefined });
        if (loginInfo.rememberMe) {
          cookies.set("rememberMe", JSON.stringify({ rememberToken: res?.data?.token, checkState: true }), { path: "/", maxAge: 30 * 24 * 60 * 60 });
        }
        toast.success(res.data.message);
        onLoginSuccess(loginInfo.userName);
        navigate("/dashboard");
      })
      .catch((error) => {
        dispatch(setLoader(false));
        setIsLoading(false);
        toast.error(error?.response?.data?.message || error.message);
      });
  };

  if (!isOpen) return null;

  return (
    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="login-modal" initial={{ scale: 0.9, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 50 }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}><FiX /></button>
        <div className="login-modal-header">
          <div className="login-modal-icon"><FiSun /></div>
          <h2>Welcome Back</h2>
          <p>Sign in to access your solar dashboard</p>
        </div>
        <div className="login-modal-form">
          <div className="form-group">
            <label>Username</label>
            <div className="input-group">
              <FiUser className="input-icon" />
              <input type="text" placeholder="Enter your username" value={loginInfo.userName}
                onChange={(e) => setLoginInfo({ ...loginInfo, userName: e.target.value })}
                onKeyPress={(e) => e.key === "Enter" && handleLogin()} />
            </div>
          </div>
          <div className="form-group">
            <label>Password</label>
            <div className="input-group">
              <FiLock className="input-icon" />
              <input type="password" placeholder="Enter your password" value={loginInfo.password}
                onChange={(e) => setLoginInfo({ ...loginInfo, password: e.target.value })}
                onKeyPress={(e) => e.key === "Enter" && handleLogin()} />
            </div>
          </div>
          <div className="form-options">
            <label className="remember-me">
              <input type="checkbox" checked={loginInfo.rememberMe} onChange={(e) => setLoginInfo({ ...loginInfo, rememberMe: e.target.checked })} />
              <span>Remember Me</span>
            </label>
          </div>
          <motion.button className="login-submit-btn" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleLogin} disabled={isLoading}>
            {isLoading ? "Signing in..." : "Sign In"}
          </motion.button>
        </div>
        <div className="login-modal-footer">
          <p>Need help? <a href="#">Contact support</a></p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Map Modal ────────────────────────────────────────────────────────────────
const MapModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="map-modal" initial={{ scale: 0.9, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 50 }} onClick={(e) => e.stopPropagation()}>
        <div className="map-modal-header">
          <h2>Solar Installation Map</h2>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="map-modal-content">
          <DeviceMapComponent showHeader={false} showFilters={false} customHeight="100%" />
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Metric Card ──────────────────────────────────────────────────────────────
const MetricCard = ({ icon, title, value, subtitle, color, trend, unit }) => (
  <motion.div className={`metric-card metric-${color}`} whileHover={{ y: -12, boxShadow: "0 20px 40px rgba(16, 185, 129, 0.25)" }} transition={{ duration: 0.3 }}>
    <motion.div className="metric-icon" whileHover={{ scale: 1.15, rotate: -15 }} transition={{ duration: 0.3 }}>{icon}</motion.div>
    <div className="metric-content">
      <h3>{title}</h3>
      <motion.div className="metric-value" layoutId={`metric-${title}`}>
        {typeof value === "number" ? value.toFixed(2) : value}
        {unit && <span className="metric-unit">{unit}</span>}
      </motion.div>
      <div className="metric-footer">
        <span className="metric-subtitle">{subtitle}</span>
        {trend && <span className="metric-trend">{trend}</span>}
      </div>
    </div>
  </motion.div>
);

// ── Status Item ──────────────────────────────────────────────────────────────
const StatusItem = ({ icon, label, value, color }) => (
  <motion.div className={`status-item status-${color}`} whileHover={{ y: -6 }} transition={{ duration: 0.2 }}>
    <motion.div className="status-icon" whileHover={{ scale: 1.2, rotate: 10 }} transition={{ duration: 0.2 }}>{icon}</motion.div>
    <div className="status-text">
      <span className="status-label">{label}</span>
      <span className="status-value">{value}</span>
    </div>
  </motion.div>
);

// ── Impact Stat ──────────────────────────────────────────────────────────────
const ImpactStat = ({ value, label, icon }) => (
  <motion.div className="impact-stat" whileHover={{ y: -8 }} transition={{ duration: 0.2 }}>
    <motion.div className="impact-stat-icon" whileHover={{ scale: 1.2, rotate: -15 }} transition={{ duration: 0.2 }}>{icon}</motion.div>
    <div>
      <div className="impact-stat-value">{value}</div>
      <div className="impact-stat-label">{label}</div>
    </div>
  </motion.div>
);

// ── Top Devices Widget ───────────────────────────────────────────────────────
const TopDevicesWidget = ({ devices, loading, onRefresh }) => {
  const [filterValue, setFilterValue] = useState("");
  const [sortBy, setSortBy] = useState("kwh");
  const topDevices = devices || [];

  const normalizeDevice = (device) => ({
    uid: device.uid || device.deviceId || device.id || "",
    beneficiaryName: device.deviceInfo?.NameOfBeneficiary || device.beneficiaryName || device.name || device.uid || "Unknown Device",
    location: device.deviceInfo?.Location || device.location || device.address || "Location N/A",
    totalPVKWh: parseFloat(device.totalPVKWh ?? device.pvKwh ?? device.energy ?? 0),
    totalCO2Saved: parseFloat(device.totalCO2Saved ?? device.co2Saved ?? device.co2 ?? 0),
    totalCarbonCredits: parseFloat(device.totalCarbonCredits ?? device.carbonCredits ?? device.credits ?? 0),
  });

  const normalized = topDevices.map(normalizeDevice);
  const sorted = [...normalized].sort((a, b) => {
    if (sortBy === "kwh") return b.totalPVKWh - a.totalPVKWh;
    if (sortBy === "co2") return b.totalCO2Saved - a.totalCO2Saved;
    if (sortBy === "credits") return b.totalCarbonCredits - a.totalCarbonCredits;
    return 0;
  });
  const filtered = sorted.filter(
    (d) => d.beneficiaryName.toLowerCase().includes(filterValue.toLowerCase()) || d.location.toLowerCase().includes(filterValue.toLowerCase()),
  );

  return (
    <motion.div className="top-devices-widget" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
      <div className="widget-header">
        <div>
          <h3><FiZap className="header-icon" /> Top Performing Homes</h3>
          <p className="widget-subtitle">सबसे अधिक सौर ऊर्जा उत्पन्न करने वाले घर</p>
        </div>
        <div className="widget-header-actions">
          <span className="device-count">Top {filtered.length} Homes</span>
          <motion.button className="widget-refresh-btn" onClick={onRefresh} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} title="Refresh devices">
            <FiRefreshCw className={loading ? "spin-icon" : ""} />
          </motion.button>
        </div>
      </div>
      <div className="widget-filter-bar">
        <div className="widget-search">
          <FiFilter className="filter-icon" />
          <input type="text" placeholder="Filter by name or location…" value={filterValue} onChange={(e) => setFilterValue(e.target.value)} />
        </div>
        <div className="widget-sort-tabs">
          {[{ key: "kwh", label: "⚡ kWh" }, { key: "co2", label: "🌿 CO₂" }, { key: "credits", label: "💎 Credits" }].map((tab) => (
            <button key={tab.key} className={`sort-tab ${sortBy === tab.key ? "active" : ""}`} onClick={() => setSortBy(tab.key)}>{tab.label}</button>
          ))}
        </div>
      </div>
      <div className="devices-list">
        {loading ? (
          <div className="loading-state"><FiRefreshCw className="spin-icon" /><p>Loading devices...</p></div>
        ) : filtered.length > 0 ? (
          filtered.map((device, idx) => {
            const progressPercent = Math.min((device.totalPVKWh / 200) * 100, 100);
            return (
              <motion.div key={`device-${device.uid || idx}`} className="device-item"
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }} viewport={{ once: true }}
                whileHover={{ y: -4, backgroundColor: "rgba(16, 185, 129, 0.08)" }}>
                <div className="device-rank">{idx === 0 && <span className="crown">👑</span>}#{idx + 1}</div>
                <div className="device-info">
                  <h4>{device.beneficiaryName}</h4>
                  <p className="device-location"><FiMap className="location-icon" /> {device.location}</p>
                </div>
                <div className="device-stats">
                  <div className="stat"><span className="label">⚡ kWh</span><span className="value">{device.totalPVKWh.toFixed(2)}</span></div>
                  <div className="stat"><span className="label">🌿 CO₂</span><span className="value">{device.totalCO2Saved.toFixed(2)}T</span></div>
                  <div className="stat highlight"><span className="label">💎 Credits</span><span className="value">{device.totalCarbonCredits.toFixed(4)}</span></div>
                </div>
                <div className="device-progress"><div className="progress-bar" style={{ width: `${progressPercent}%` }} /></div>
              </motion.div>
            );
          })
        ) : (
          <div className="empty-state"><FiAlertCircle /><p>No devices match your filter</p></div>
        )}
      </div>
    </motion.div>
  );
};

// ── Top Donors Widget ────────────────────────────────────────────────────────
const TopDonorsWidget = ({ donors, loading }) => {
  const topDonors = donors || [];
  return (
    <motion.div className="top-donors-widget" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
      <div className="widget-header">
        <div>
          <h3><FiUsers className="header-icon" /> Top Contributing Donors</h3>
          <p className="widget-subtitle">Doners:-ग्रामीण और दूरस्थ क्षेत्रों में सौर ऊर्जा पहुँचाकर जीवन में रोशनी ला रहे हैं।</p>
        </div>
      </div>
      <div className="donors-list">
        {loading ? (
          <div className="loading-state"><FiRefreshCw className="spin-icon" /><p>Loading partners...</p></div>
        ) : topDonors.length > 0 ? (
          topDonors.map((donor, idx) => (
            <motion.div key={`donor-${donor.donorId || idx}`} className="donor-item"
              initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }} viewport={{ once: true }}
              whileHover={{ y: -4, backgroundColor: "rgba(16, 185, 129, 0.08)" }}>
              <div className="donor-rank">#{idx + 1}</div>
              <div className="donor-info">
                <h4>{donor.donorName || "Unknown Partner"}</h4>
                <p className="donor-contribution">{donor.deviceCount} {donor.deviceCount === 1 ? "installation" : "installations"}</p>
              </div>
              <div className="donor-stats">
                <div className="stat"><span className="label">Homes</span><span className="value">{donor.deviceCount || 0}</span></div>
                <div className="stat"><span className="label">⚡ kWh</span><span className="value">{(donor.totalPVKWh || 0).toFixed(2)}</span></div>
                <div className="stat"><span className="label">🌿 CO₂</span><span className="value">{(donor.totalCO2Saved || 0).toFixed(2)}T</span></div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="empty-state"><FiAlertCircle /><p>No partner data available</p></div>
        )}
      </div>
    </motion.div>
  );
};

// ── Live Energy Flow ─────────────────────────────────────────────────────────
const LiveEnergyFlow = ({ currentOutput }) => (
  <motion.div className="energy-flow-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
    <div className="card-shimmer" />
    <div className="energy-flow-header">
      <h3><FiActivity className="pulse-icon" /> Live Energy Flow</h3>
      <motion.div className="live-indicator" animate={{ opacity: [1, 0.6, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
        <span className="live-dot" /> LIVE
      </motion.div>
    </div>
    <div className="energy-flow-visual">
      <div className="beam-container left">
        <div className="beam-track">
          <div className="h-beam" />
          {[...Array(5)].map((_, i) => <div key={`lp-${i}`} className={`particle p${i + 1}`} />)}
          <div className="bars-wrapper">{[1,2,3,4,5,6,7].map((n) => <div key={n} className={`bar b${n}`} />)}</div>
        </div>
      </div>
      <div className="energy-center">
        <div className="img-ring ring1" /><div className="img-ring ring2" /><div className="img-ring ring3" />
        <img src="/solar.png" alt="solar energy" className="solar-energy-img" />
      </div>
      <div className="beam-container right">
        <div className="beam-track">
          <div className="h-beam" />
          {[...Array(5)].map((_, i) => <div key={`rp-${i}`} className={`particle p${i + 1}`} />)}
          <div className="bars-wrapper">{[1,2,3,4,5,6,7].map((n) => <div key={n} className={`bar b${n}`} />)}</div>
        </div>
      </div>
    </div>
    <div className="energy-stats">
      <div className="energy-stat">
        <span className="label">Current Output</span>
        <motion.span className="value" animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}>
          {currentOutput.toFixed(2)} kW
        </motion.span>
      </div>
      <div className="stat-divider" />
      <div className="energy-stat">
        <span className="label">Today's Peak</span>
        <span className="value">{(currentOutput * 1.25).toFixed(2)} kW</span>
      </div>
    </div>
  </motion.div>
);

// ── Monthly Chart Widget ─────────────────────────────────────────────────────
const MonthlyChartWidget = ({ monthlyTrend }) => {
  const [period, setPeriod] = useState("all");
  const [chartType, setChartType] = useState("area");

  const slicedTrend = (() => {
    if (!monthlyTrend || monthlyTrend.length === 0) return [];
    if (period === "1m") return monthlyTrend.slice(-1);
    if (period === "2m") return monthlyTrend.slice(-2);
    return monthlyTrend;
  })();

  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const categories = slicedTrend.map((m) => {
    if (!m.month || !m.month.includes("-")) return m.month || "";
    const [year, month] = m.month.split("-");
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  });
  const seriesData = slicedTrend.map((m) => parseFloat((m.totalPVKWh || 0).toFixed(2)));

  const commonOptions = {
    chart: { type: chartType, toolbar: { show: false }, fontFamily: "inherit", background: "transparent", animations: { enabled: true, speed: 600 } },
    colors: ["#10b981"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: chartType === "bar" ? 0 : 3, colors: ["#10b981"] },
    xaxis: { categories, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#9ca3af", fontSize: "12px" } } },
    yaxis: { labels: { style: { colors: "#9ca3af", fontSize: "12px" } }, axisBorder: { show: false } },
    grid: { borderColor: "rgba(16, 185, 129, 0.15)", strokeDashArray: 4 },
    tooltip: { theme: "dark", y: { formatter: (v) => `${v.toFixed(1)} kWh` } },
    fill: chartType === "area"
      ? { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [20, 100] } }
      : chartType === "bar"
      ? { type: "gradient", gradient: { shade: "dark", type: "vertical", shadeIntensity: 0.3, gradientToColors: ["#06b6d4"], opacityFrom: 0.9, opacityTo: 0.7 } }
      : { type: "solid", opacity: 0 },
    plotOptions: chartType === "bar" ? { bar: { borderRadius: 6, columnWidth: "55%" } } : {},
  };

  return (
    <motion.div className="chart-container large" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
      <div className="chart-header chart-header-with-controls">
        <div>
          <h2>📈 Monthly Power Generation Trend</h2>
          <p className="chart-subtitle">Energy output progression over time</p>
        </div>
        <div className="chart-controls">
          <div className="chart-filter-group">
            <FiFilter className="ctrl-icon" />
            {[{ key: "1m", label: "1 Month" }, { key: "2m", label: "2 Months" }, { key: "all", label: "Yearly" }].map((opt) => (
              <button key={opt.key} className={`ctrl-tab ${period === opt.key ? "active" : ""}`} onClick={() => setPeriod(opt.key)}>{opt.label}</button>
            ))}
          </div>
          <div className="chart-filter-group">
            {[{ key: "area", label: "Area" }, { key: "bar", label: "Bar" }, { key: "line", label: "Line" }].map((opt) => (
              <button key={opt.key} className={`ctrl-tab ${chartType === opt.key ? "active" : ""}`} onClick={() => setChartType(opt.key)}>{opt.label}</button>
            ))}
          </div>
        </div>
      </div>
      {seriesData.length > 0 ? (
        <Chart key={`${chartType}-${period}`} options={commonOptions} series={[{ name: "Power Generation (kWh)", data: seriesData }]} type={chartType} height={350} />
      ) : (
        <div className="empty-state" style={{ height: 350, justifyContent: "center" }}><FiAlertCircle /><p>No data available for selected period</p></div>
      )}
    </motion.div>
  );
};

// ── Main Dashboard ───────────────────────────────────────────────────────────
const SolarDashboardRegister = () => {
  const cookies = new Cookies();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [currentOutput, setCurrentOutput] = useState(12.45);
  const [dashboardData, setDashboardData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);

  const notifiedDevices = useRef(new Set());
  const repeatRefreshRef = useRef(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardRes, devicesRes] = await Promise.all([
        axios.get(apiEndpoints.carbonDashboard),
        axios.get(apiEndpoints.devices),
      ]);

      const newDevices = devicesRes.data?.info || [];
      setDashboardData(dashboardRes.data?.data);
      setDevices(newDevices);
      setCurrentOutput(8 + Math.random() * 10);

      // ── Notifications — ONE loop, from topDevices (has correct names/locations) ──
      const topDevicesForNotify = dashboardRes.data?.data?.topDevices || [];

      topDevicesForNotify.forEach((device) => {
        const kwh = parseFloat(device.totalPVKWh || 0);
        const uid = device.uid || "";

        // Skip already notified this session
        if (notifiedDevices.current.has(uid)) return;

        // Skip corrupt data (>50 kWh/day impossible for home device)
        if (kwh > 50) return;

        // Only notify above 2 kWh
        if (kwh > 2) {
          notifiedDevices.current.add(uid);

          const name = device.deviceInfo?.NameOfBeneficiary?.trim() || uid || "Unknown";
          const location = device.deviceInfo?.Location?.trim() || "";
          const village = device.deviceInfo?.VillageName?.trim() || "";

          toast(
            (t) => (
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", minWidth: "240px" }}>
                <span style={{ fontSize: "1.3rem", flexShrink: 0 }}>🌞</span>
                <div style={{ flex: 1, fontSize: "0.83rem", lineHeight: "1.6" }}>
                  <div style={{ fontWeight: 700, color: "#10b981", marginBottom: "2px" }}>
                    ⚡ {name}
                  </div>
                  {(location || village) && (
                    <div style={{ color: "#94a3b8", fontSize: "0.74rem", marginBottom: "3px" }}>
                      📍 {location}{village ? ` · ${village}` : ""}
                    </div>
                  )}
                  <div style={{ color: "#e2e8f0" }}>
                    Generated{" "}
                    <strong style={{ color: "#6ee7b7" }}>{kwh.toFixed(2)} kWh</strong>{" "}
                    today
                  </div>
                </div>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    borderRadius: "50%",
                    width: "22px",
                    height: "22px",
                    minWidth: "22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#94a3b8",
                    fontSize: "0.65rem",
                    flexShrink: 0,
                    padding: 0,
                    lineHeight: 1,
                  }}
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            ),
            {
              duration: Infinity,
              style: {
                background: "#0f172a",
                color: "#f8fafc",
                border: "1px solid rgba(16,185,129,0.35)",
                borderRadius: "12px",
                padding: "12px 14px",
                maxWidth: "320px",
                boxShadow: "0 0 24px rgba(16,185,129,0.18), 0 4px 16px rgba(0,0,0,0.4)",
              },
            }
          );
        }
      });

      if (!isLoggedIn) {
        toast.success("Data updated successfully!", {
          style: { background: "#0f172a", color: "#f8fafc", border: "1px solid rgba(16,185,129,0.35)" },
        });
      }
    } catch (err) {
      setIsDismissed(false);
      setError(err.response?.data?.message || "Failed to fetch dashboard data. Please try again.");
      toast.error("Error loading dashboard data", {
        style: { background: "#0f172a", color: "#f8fafc", border: "1px solid rgba(239,68,68,0.4)" },
      });
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Live kW simulation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentOutput((prev) => Math.min(Math.max(prev + (Math.random() - 0.5) * 2, 5), 25));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load on mount → 10s first refresh → 5 min repeat
  useEffect(() => {
    fetchDashboardData();

    const rememberMeCookie = cookies.get("rememberMe");
    if (rememberMeCookie !== undefined) {
      const rm = typeof rememberMeCookie === "string" ? JSON.parse(rememberMeCookie) : rememberMeCookie;
      if (rm?.checkState === true) {
        const adminToken = cookies.get("adminToken");
        if (adminToken) { setIsLoggedIn(true); setUserName("Admin"); }
      }
    }

    const firstRefresh = setTimeout(() => {
      fetchDashboardData();
      repeatRefreshRef.current = setInterval(() => { fetchDashboardData(); }, 300000);
    }, 10000);

    return () => {
      clearTimeout(firstRefresh);
      clearInterval(repeatRefreshRef.current);
    };
  }, []);

  // Scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const handleLoginSuccess = (username) => { setIsLoggedIn(true); setUserName(username); };
  const handleLogout = () => {
    cookies.remove("adminToken"); cookies.remove("rememberMe");
    setIsLoggedIn(false); setUserName("");
    toast.success("Logged out successfully");
  };
  const handleManualRefresh = () => { fetchDashboardData(); toast.success("Data refreshing..."); };

  const summary = dashboardData?.summary || {};
  const monthlyTrend = dashboardData?.monthlyTrend || [];
  const topDonors = dashboardData?.topDonors || [];
  const topDevicesFromDashboard = dashboardData?.topDevices || [];
  const totalTrees = summary.equivalentTrees || 0;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <div className="solar-dashboard">
      <motion.div className="scroll-progress-bar" style={{ scaleX: scrollProgress / 100 }} initial={{ scaleX: 0 }} transition={{ duration: 0.2 }} />

      {/* Error Banner */}
      <AnimatePresence>
        {error && !isDismissed && (
          <motion.div key="error-banner" className="error-banner"
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
            <FiAlertCircle />
            <span>{error}</span>
            <button onClick={fetchDashboardData}>Retry</button>
            <button className="error-dismiss-btn" onClick={() => setIsDismissed(true)} title="Dismiss"><FiX /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.header className="dashboard-header" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="header-content">
          <div className="header-info">
            <div className="logo-section">
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="logo-wrapper">
                <img src="/lum.png" alt="logo" className="header-logo" />
              </motion.div>
            </div>
          </div>
          <div className="header-actions">
            <motion.button className="refresh-btn" onClick={handleManualRefresh} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} title="Refresh data">
              <FiRefreshCw className={loading ? "spin-icon" : ""} />
            </motion.button>
            {isLoggedIn ? (
              <motion.div className="user-menu" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                <div className="user-info">
                  <span className="user-name">{userName}</span>
                  <button className="logout-btn" onClick={handleLogout}>Logout</button>
                </div>
              </motion.div>
            ) : (
              <motion.button className="header-btn login-btn" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }} onClick={() => setIsLoginModalOpen(true)}>
                <FiLogIn /><span>Login</span>
              </motion.button>
            )}
            <motion.div className="status-badge active" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 }}
              onClick={() => setIsMapModalOpen(true)} style={{ cursor: "pointer" }} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <motion.span className="pulsee" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }} />
              Live Map
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Modals */}
      <AnimatePresence>
        {isLoginModalOpen && <LoginModal key="login-modal" isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} onLoginSuccess={handleLoginSuccess} />}
        {isMapModalOpen && <MapModal key="map-modal" isOpen={isMapModalOpen} onClose={() => setIsMapModalOpen(false)} />}
      </AnimatePresence>

      {/* Main */}
      <main className="dashboard-main">

        {isLoggedIn && (
          <motion.div className="welcome-banner" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="welcome-content">
              <RiLeafLine className="welcome-icon" />
              <div>
                <h3>Welcome back, {userName}!</h3>
                <p>Total carbon offset: {summary.totalCO2Saved?.toFixed(2)} tons CO₂ — equivalent to planting {Math.floor(totalTrees)} trees! 🌳</p>
              </div>
            </div>
          </motion.div>
        )}

        <LiveEnergyFlow currentOutput={currentOutput} />

        <motion.section className="metrics-grid" variants={containerVariants} initial="hidden" animate="visible">
          {[
            { icon: <FiZap />, title: "Total Power Generated", value: summary.totalPVKWh, subtitle: "All time", color: "primary", unit: "kWh", trend: `${summary.activeDevices} active` },
            { icon: <RiEarthLine />, title: "CO₂ Offset", value: summary.totalCO2Saved, subtitle: "Total reduction", color: "success", unit: "Tons", trend: "↓ emissions" },
            { icon: <RiPlantLine />, title: "Trees Equivalent", value: summary.equivalentTrees, subtitle: "Carbon absorbed", color: "eco", unit: "trees", trend: "Growing" },
            { icon: <FiAward />, title: "Carbon Credits", value: summary.totalCarbonCredits, subtitle: "Earned", color: "secondary", unit: "Credits", trend: `${summary.totalDevices} devices` },
          ].map((metric, idx) => (
            <motion.div key={`metric-${idx}`} variants={itemVariants}><MetricCard {...metric} /></motion.div>
          ))}
        </motion.section>

        <motion.div className="status-overview" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <motion.div className="status-card" whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(16, 185, 129, 0.2)" }} transition={{ duration: 0.3 }}>
            <div className="status-header">
              <h3>System Overview</h3>
              <motion.div className="status-indicator" animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                <motion.span className="pulsee-dot" animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity }} />
                <span>Live</span>
              </motion.div>
            </div>
            <motion.div className="status-grid" variants={containerVariants} initial="hidden" animate="visible">
              {[
                { icon: <FiCheckCircle />, label: "Active Devices", value: summary.activeDevices || 0, color: "primary" },
                { icon: <FiHome />, label: "Total Homes", value: summary.totalDevices || 0, color: "success" },
                { icon: <FiAlertCircle />, label: "Inactive", value: summary.inactiveDevices || 0, color: "secondary" },
                { icon: <FiTrendingUp />, label: "Success Rate", value: summary.totalDevices > 0 ? `${((summary.activeDevices / summary.totalDevices) * 100).toFixed(1)}%` : "0%", color: "eco" },
              ].map((item, idx) => (
                <motion.div key={`status-${idx}`} variants={itemVariants}><StatusItem {...item} /></motion.div>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>

        <div className="charts-section charts-section-single">
          {monthlyTrend.length > 0 && <MonthlyChartWidget monthlyTrend={monthlyTrend} />}
        </div>

        <div className="devices-partners-section">
          <TopDevicesWidget devices={topDevicesFromDashboard} loading={loading} onRefresh={handleManualRefresh} />
          <TopDonorsWidget donors={topDonors} loading={loading} />
        </div>

        <motion.div className="impact-card full-width" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(16, 185, 129, 0.2)" }}>
          <div className="impact-header">
            <RiLeafLine className="impact-icon" />
            <h3>🌱 Total Environmental Impact</h3>
          </div>
          <motion.div className="impact-stats" variants={containerVariants} initial="hidden" animate="visible">
            {[
              { value: summary.equivalentTrees?.toFixed(2) || "0", label: "Equivalent Trees Planted", icon: <RiPlantLine /> },
              { value: `₹${((summary.totalCO2Saved || 0) * 500).toLocaleString("en-IN")}`, label: "Estimated Ecological Value", icon: <FiTrendingUp /> },
              { value: `${summary.totalCO2Saved?.toFixed(2) || 0}T`, label: "CO₂ Offset", icon: <RiEarthLine /> },
              { value: `${summary.totalCarbonCredits?.toFixed(4) || 0}`, label: "Total Carbon Credits", icon: <FiAward /> },
            ].map((stat, idx) => (
              <motion.div key={`impact-${idx}`} variants={itemVariants}><ImpactStat {...stat} /></motion.div>
            ))}
          </motion.div>
        </motion.div>

        <footer className="footer-section">
          <div className="footer-content">
            <div className="footer-text">
              <h2>☀️ Making renewable energy transparent and impactful</h2>
              <p>Monitor your solar installations in real-time and track your contribution to a sustainable future.</p>
            </div>
            <motion.button className="footer-button" whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }} onClick={scrollToTop}>
              Back to Top <FiArrowRight />
            </motion.button>
          </div>
          <div className="footer-bottom">
            <p>© 2026 Bindi Solar Energy Monitor. All rights reserved. | Powered by Clean Energy</p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default SolarDashboardRegister;